import * as React from 'react';
import { IFrontendModule, RenderComponent } from '../../../../GStore.Modules/FrontEnd';
import { IdentityState } from '../store/identity';

export default class ModuleManager {
    // TODO: hacer identity obligatorio, borrando las instancias manuales del moduleManager,
    // utilizando solo la instancia del store
    private id: String = 'init';

    constructor(
        private modules: IFrontendModule[],
        private identity: IdentityState | null = null,
        aId: String | null = null
    ) {
        this.id = aId ?? this.id;
    }

    public getModule(name: string) {
        return this.modules.find(m => m.name === name);
    }

    public getId() {
        return this.id;
    }

    public componentProperties<T>(key: string, defaultValue: T | undefined = undefined) {
        const filterModules = this
            .modules
            .filter(m => m.componentProperties);

        const result = filterModules
            .reduce(
                (value: any, module: IFrontendModule) =>
                    value !== undefined ? value : module.componentProperties[key] as T,
                undefined) as T | undefined;

        const final = result ?? defaultValue;

        console.log('[ModuleManager.componentProperties] key=', key,
            'defaultValue=', defaultValue,
            'filterModules=', filterModules.map(m => ({ name: m.name, keys: Object.keys(m.componentProperties ?? {}) })),
            'result=', result,
            'final=', final);

        return final;
    }

    public useComponentProperties<T>(key: string, defaultValue: T | undefined = undefined) {
        const [value, setValue] = React.useState(defaultValue);

        React.useEffect(() => {
            setValue(this.componentProperties(key, defaultValue));
        }, [this.modules]);

        return value;
    }

    public renderComponent<T>(
        key: string,
        ctx: any,
        originalComponent: React.FunctionComponent<T>,
        force: boolean = false) {
        const modules = this.modules;

        const specificModule: RenderComponent | undefined = modules
            .filter(m => m.name !== 'FrontendCommon')
            .map(m => m.renderComponent?.(key, ctx))
            .find(m => m !== undefined && m !== null);

        if (specificModule) {
            const component = specificModule(ctx);
            if (component) {
                return component;
            }
        }

        // No specific module override - try FrontendCommon as the general fallback
        const commonModule: RenderComponent | undefined = modules
            .filter(m => m.name === 'FrontendCommon')
            .map(m => m.renderComponent?.(key, ctx))
            .find(m => m !== undefined && m !== null);

        if (commonModule) {
            const component = commonModule(ctx);
            if (component) {
                return component;
            }
        }

        // No module provides the component - use the original/default component
        const component = originalComponent(ctx);

        // no devolvemos el componente original
        // salvo que ya esten cargados los modulos o el parametro force == true
        if (this.id !== 'init' || force) {
            return component;
        }
        else {
            return <img
                alt='Loading...'
                style={{ width: '32px', height: '32px' }}
                src='/img/loading.gif' />;
        }
    }

    public filterDependency<T>(
        key: string,
        ctx: any | undefined = {}) {
        const dependencyCtx = {
            ...ctx,
            identity: this.identity,
        };

        const filterModules = this
            .modules
            .filter(m => m.filterDependency);

        return (d: T) =>
            filterModules
                .reduce(
                    (value: boolean, module: IFrontendModule) =>
                        value && module.filterDependency!(key, dependencyCtx)(d),
                    true);
    }

    public injectComponents(
        key: string,
        ctx: any,
        dependencies: any) {
        for (const m of this.modules) {
            m.injectComponent?.(key, ctx, dependencies);
        }
    }

    public requirePermissionMemo(
        key: string,
        ctx: any,
        defaultValue: boolean | undefined,
        deps: any[],
    ) {
        const [value, setValue] = React.useState(defaultValue);

        const load = async () => {
            const p = await this.requirePermission(key, ctx, defaultValue);
            setValue(p);
        };

        React.useEffect(() => {
            load();
        }, deps);

        return value;
    }

    public async requirePermission(key: string, ctx: any, defaultValue: boolean | undefined) {
        const filterModules = this
            .modules
            .filter(m => m.hasPermission);

        if (filterModules != null && filterModules.length > 0) {
            let res = true;
            for (const f of filterModules) {
                const moduleRes = await awaitIfPromise(f.hasPermission!(key, ctx, defaultValue));
                res = res && (moduleRes ?? true);
            }
            return res;
        }
        else {
            return defaultValue;
        }
    }

    public async hasPermission(key: string, ctx: any) {
        const filterModules = this
            .modules
            .filter(m => m.hasPermission);

        let res = true;
        for (const f of filterModules) {
            res = res && (await awaitIfPromise(f.hasPermission!(key, ctx, undefined)) ?? true);
        }

        return res;
    }

    public injectDataColumns<T>(name: string, ctx: T) {
        // consultamos a los módulos si tienen alguna columna que mostrar
        const columns = [];

        for (const m of this.modules) {
            if (m.injectDataColumns) {
                columns.push(...m.injectDataColumns(name, ctx));
            }
        }

        return columns;
    }

    public transformTableColumns<T>(name: string, ctx: T, defaultColumns: any[]) {
        // consultamos a los módulos si tienen una configuración de columnas para esta tabla
        for (const m of this.modules) {
            if (m.transformTableColumns) {
                const columns = m.transformTableColumns(name, ctx, defaultColumns);
                if (columns !== undefined && columns !== null) {
                    return columns;
                }
            }
        }

        return defaultColumns;
    }

    public injectAction<T>(name: string, ctx: T) {
        for (const m of this.modules) {
            if (m.injectAction) {
                return m.injectAction(name, ctx);
            }
        }
    }

    public format<T>(key: string, data: T, ctx: any | undefined = undefined) {
        const filterModules = this
            .modules
            .filter(m => m.format != null);

        if (filterModules != null) {
            let res = true;
            for (const f of filterModules) {
                const moduleRes = f.format!(key, data, ctx);
                if (moduleRes) {
                    return moduleRes;
                }
            }
        }

        return undefined;
    }

    public validateData<T>(key: string, data: T, ctx: any | undefined = undefined) {
        const filterModules = this
            .modules
            .filter(m => m.validateData != null);

        if (filterModules != null) {

            for (const f of filterModules) {
                const moduleRes = f.validateData!(key, data, ctx);
                if (moduleRes?.valueOf) {
                    return moduleRes;
                }
            }
        }

        return true;
    }

    public async getMenuItems(ctx: any) {
        const items = [];

        for (const m of this.modules) {
            if (m.getMenuItems) {
                const receivedItems = await awaitIfPromise(m.getMenuItems(ctx));
                items.push(...receivedItems);
                // items.push(...m.getMenuItems(ctx));
            }
        }

        return items;
    }

    public async getMessages(ctx : any) {
        const messages = [];

        for (const m of this.modules) {
            if (m.getMessages) {
                const receivedMessages = await awaitIfPromise(m.getMessages(ctx));
                messages.push(...receivedMessages);
            }
        }

        return messages;
    }
}

function awaitIfPromise<T>(arg0: T | Promise<T>): Promise<T> {
    if (arg0 instanceof Promise) {
        return arg0;
    }
    else {
        return Promise.resolve(arg0);
    }
}
